/**
 * =============================================================================
 * MLYN BASE JS - Reusable JavaScript Utilities
 * =============================================================================
 * File ini untuk JavaScript yang reusable di semua section Mlyn
 * - Animations & Observers
 * - Common interactions
 * - Helper functions
 * - Shared utilities
 * 
 * JavaScript yang spesifik untuk satu section → taruh inline di section liquid
 * =============================================================================
 */

// =============================================================================
// INTERSECTION OBSERVER - Animate on Scroll
// =============================================================================

/**
 * Initialize fade-in animations on scroll
 * Usage: Add class "mlyn-animate-on-scroll" to any element
 */
function mlynInitScrollAnimations() {
  const elements = document.querySelectorAll('.mlyn-animate-on-scroll');
  
  if (!elements.length) return;
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('mlyn-visible');
        // Optional: unobserve after animation
        // observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  });
  
  elements.forEach(el => observer.observe(el));
}

// =============================================================================
// LAZY LOADING - Images & Videos
// =============================================================================

/**
 * Lazy load images with data-src attribute
 * Usage: <img data-src="image.jpg" class="mlyn-lazy" />
 */
function mlynInitLazyLoad() {
  const lazyElements = document.querySelectorAll('.mlyn-lazy[data-src]');
  
  if (!lazyElements.length) return;
  
  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const src = element.getAttribute('data-src');
        
        if (element.tagName === 'IMG') {
          element.src = src;
        } else if (element.tagName === 'VIDEO') {
          element.src = src;
          element.load();
        } else {
          element.style.backgroundImage = `url('${src}')`;
        }
        
        element.classList.remove('mlyn-lazy');
        element.classList.add('mlyn-loaded');
        imageObserver.unobserve(element);
      }
    });
  });
  
  lazyElements.forEach(el => imageObserver.observe(el));
}

// =============================================================================
// PARALLAX EFFECT
// =============================================================================

/**
 * Simple parallax effect on scroll
 * Usage: Add class "mlyn-parallax" with data-speed attribute
 * Example: <div class="mlyn-parallax" data-speed="0.5">...</div>
 */
function mlynInitParallax() {
  const parallaxElements = document.querySelectorAll('.mlyn-parallax');
  
  if (!parallaxElements.length) return;
  
  let ticking = false;
  
  function updateParallax() {
    const scrolled = window.pageYOffset;
    
    parallaxElements.forEach(el => {
      const speed = el.getAttribute('data-speed') || 0.5;
      const yPos = -(scrolled * speed);
      el.style.transform = `translateY(${yPos}px)`;
    });
    
    ticking = false;
  }
  
  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(updateParallax);
      ticking = true;
    }
  });
}

// =============================================================================
// ACCORDION
// =============================================================================

/**
 * Simple accordion functionality
 * Usage: 
 * <div class="mlyn-accordion">
 *   <button class="mlyn-accordion-trigger">Title</button>
 *   <div class="mlyn-accordion-content">Content</div>
 * </div>
 */
function mlynInitAccordion() {
  const accordions = document.querySelectorAll('.mlyn-accordion');
  
  accordions.forEach(accordion => {
    const trigger = accordion.querySelector('.mlyn-accordion-trigger');
    const content = accordion.querySelector('.mlyn-accordion-content');
    
    if (!trigger || !content) return;
    
    trigger.addEventListener('click', () => {
      const isOpen = accordion.classList.contains('mlyn-active');
      
      // Close all accordions in same group (optional)
      const group = accordion.closest('.mlyn-accordion-group');
      if (group) {
        group.querySelectorAll('.mlyn-accordion').forEach(item => {
          item.classList.remove('mlyn-active');
          item.querySelector('.mlyn-accordion-content').style.maxHeight = null;
        });
      }
      
      // Toggle current
      if (!isOpen) {
        accordion.classList.add('mlyn-active');
        content.style.maxHeight = content.scrollHeight + 'px';
      } else {
        accordion.classList.remove('mlyn-active');
        content.style.maxHeight = null;
      }
    });
  });
}

// =============================================================================
// TABS
// =============================================================================

/**
 * Tab switching functionality
 * Usage:
 * <div class="mlyn-tabs">
 *   <button class="mlyn-tab" data-tab="tab1">Tab 1</button>
 *   <button class="mlyn-tab" data-tab="tab2">Tab 2</button>
 * </div>
 * <div class="mlyn-tab-content" data-tab="tab1">Content 1</div>
 * <div class="mlyn-tab-content" data-tab="tab2">Content 2</div>
 */
function mlynInitTabs() {
  const tabContainers = document.querySelectorAll('.mlyn-tabs');
  
  tabContainers.forEach(container => {
    const tabs = container.querySelectorAll('.mlyn-tab');
    
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const targetTab = tab.getAttribute('data-tab');
        
        // Remove active class from all tabs
        tabs.forEach(t => t.classList.remove('mlyn-active'));
        tab.classList.add('mlyn-active');
        
        // Hide all content, show target
        const contents = document.querySelectorAll(`.mlyn-tab-content[data-tab]`);
        contents.forEach(content => {
          if (content.getAttribute('data-tab') === targetTab) {
            content.classList.remove('tw-hidden');
            content.classList.add('mlyn-active');
          } else {
            content.classList.add('tw-hidden');
            content.classList.remove('mlyn-active');
          }
        });
      });
    });
  });
}

// =============================================================================
// MODAL
// =============================================================================

/**
 * Simple modal functionality
 * Usage:
 * <button data-mlyn-modal="modal-id">Open Modal</button>
 * <div id="modal-id" class="mlyn-modal">
 *   <div class="mlyn-modal-content">
 *     <button class="mlyn-modal-close">&times;</button>
 *     Content here
 *   </div>
 * </div>
 */
function mlynInitModal() {
  // Open modal
  document.querySelectorAll('[data-mlyn-modal]').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const modalId = trigger.getAttribute('data-mlyn-modal');
      const modal = document.getElementById(modalId);
      if (modal) {
        modal.classList.add('mlyn-active');
        document.body.style.overflow = 'hidden';
      }
    });
  });
  
  // Close modal
  document.querySelectorAll('.mlyn-modal-close, .mlyn-modal-overlay').forEach(closeBtn => {
    closeBtn.addEventListener('click', () => {
      const modal = closeBtn.closest('.mlyn-modal');
      if (modal) {
        modal.classList.remove('mlyn-active');
        document.body.style.overflow = '';
      }
    });
  });
  
  // Close on ESC key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.mlyn-modal.mlyn-active').forEach(modal => {
        modal.classList.remove('mlyn-active');
        document.body.style.overflow = '';
      });
    }
  });
}

// =============================================================================
// SMOOTH SCROLL
// =============================================================================

/**
 * Smooth scroll to anchor links
 * Usage: <a href="#section-id" class="mlyn-smooth-scroll">Link</a>
 */
function mlynInitSmoothScroll() {
  document.querySelectorAll('a.mlyn-smooth-scroll[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const targetId = this.getAttribute('href');
      const target = document.querySelector(targetId);
      
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });
}

// =============================================================================
// COUNTDOWN TIMER
// =============================================================================

/**
 * Countdown timer utility
 * Usage: <div class="mlyn-countdown" data-date="2024-12-31T23:59:59"></div>
 */
function mlynInitCountdown() {
  const countdowns = document.querySelectorAll('.mlyn-countdown[data-date]');
  
  countdowns.forEach(countdown => {
    const endDate = new Date(countdown.getAttribute('data-date')).getTime();
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = endDate - now;
      
      if (distance < 0) {
        countdown.innerHTML = '<span class="mlyn-countdown-expired">Expired</span>';
        return;
      }
      
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      countdown.innerHTML = `
        <span class="mlyn-countdown-item">${days}d</span>
        <span class="mlyn-countdown-item">${hours}h</span>
        <span class="mlyn-countdown-item">${minutes}m</span>
        <span class="mlyn-countdown-item">${seconds}s</span>
      `;
    };
    
    updateCountdown();
    setInterval(updateCountdown, 1000);
  });
}

// =============================================================================
// DEBOUNCE & THROTTLE UTILITIES
// =============================================================================

/**
 * Debounce function - delays execution until after delay has passed
 */
function mlynDebounce(func, delay = 300) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

/**
 * Throttle function - limits execution to once per delay period
 */
function mlynThrottle(func, delay = 300) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func.apply(this, args);
    }
  };
}

// =============================================================================
// FORM VALIDATION
// =============================================================================

/**
 * Simple form validation
 * Usage: Add "mlyn-validate" class to form
 */
function mlynInitFormValidation() {
  const forms = document.querySelectorAll('form.mlyn-validate');
  
  forms.forEach(form => {
    form.addEventListener('submit', (e) => {
      let isValid = true;
      
      // Clear previous errors
      form.querySelectorAll('.mlyn-error').forEach(error => error.remove());
      
      // Required fields
      form.querySelectorAll('[required]').forEach(field => {
        if (!field.value.trim()) {
          isValid = false;
          const error = document.createElement('span');
          error.className = 'mlyn-error tw-text-red-500 tw-text-sm tw-mt-1';
          error.textContent = field.getAttribute('data-error') || 'This field is required';
          field.parentNode.appendChild(error);
          field.classList.add('tw-border-red-500');
        }
      });
      
      // Email validation
      form.querySelectorAll('input[type="email"]').forEach(field => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (field.value && !emailRegex.test(field.value)) {
          isValid = false;
          const error = document.createElement('span');
          error.className = 'mlyn-error tw-text-red-500 tw-text-sm tw-mt-1';
          error.textContent = 'Please enter a valid email';
          field.parentNode.appendChild(error);
          field.classList.add('tw-border-red-500');
        }
      });
      
      if (!isValid) {
        e.preventDefault();
      }
    });
  });
}

// =============================================================================
// COPY TO CLIPBOARD
// =============================================================================

/**
 * Copy text to clipboard
 * Usage: <button class="mlyn-copy" data-copy="text to copy">Copy</button>
 */
function mlynInitCopyToClipboard() {
  document.querySelectorAll('.mlyn-copy[data-copy]').forEach(btn => {
    btn.addEventListener('click', () => {
      const text = btn.getAttribute('data-copy');
      navigator.clipboard.writeText(text).then(() => {
        const originalText = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => {
          btn.textContent = originalText;
        }, 2000);
      });
    });
  });
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize all Mlyn utilities
 * Auto-runs on DOMContentLoaded and on Shopify section load
 */
function mlynInit() {
  mlynInitScrollAnimations();
  mlynInitLazyLoad();
  mlynInitParallax();
  mlynInitAccordion();
  mlynInitTabs();
  mlynInitModal();
  mlynInitSmoothScroll();
  mlynInitCountdown();
  mlynInitFormValidation();
  mlynInitCopyToClipboard();
}

// Auto-init on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mlynInit);
} else {
  mlynInit();
}

// Re-init on Shopify section load (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', mlynInit);
}

// Export functions for manual use
window.Mlyn = {
  init: mlynInit,
  scrollAnimations: mlynInitScrollAnimations,
  lazyLoad: mlynInitLazyLoad,
  parallax: mlynInitParallax,
  accordion: mlynInitAccordion,
  tabs: mlynInitTabs,
  modal: mlynInitModal,
  smoothScroll: mlynInitSmoothScroll,
  countdown: mlynInitCountdown,
  formValidation: mlynInitFormValidation,
  copyToClipboard: mlynInitCopyToClipboard,
  debounce: mlynDebounce,
  throttle: mlynThrottle
};
